import { ComponentChildren, JSX } from 'preact';

import { classNames } from '#utils/UNSAFE_classNames';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { Size } from '#utils/UNSAFE_size';
import {
  labelSlotStyles,
  labelInnerStyles
} from '../UNSAFE_LabelValueLayout/LabelValueLayoutStyles.css';

import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { RadioRedwoodTheme } from '#UNSAFE_RadioItem/themes/redwood/RadioTheme';
import { LabelRedwoodTheme } from '#UNSAFE_Label/themes/redwood/LabelTheme';
import { FormLayoutRedwoodTheme } from '#UNSAFE_TextField/themes/redwood/FormLayoutTheme';
import { CheckboxRedwoodTheme } from '#UNSAFE_Checkbox/themes/redwood/CheckboxTheme';
import { useRichSelectionContext } from '#PRIVATE_RichSelectionCommon/RichSelectionContext';
import { StyledLabelValueLayout } from '../UNSAFE_LabelValueLayout/StyledLabelValueLayout';
import { richLabelInnerStyles } from './RichLabelValueLayoutStyles.css';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { computeStartLabelWidth } from '#utils/UNSAFE_formControlUtils';

type Props = {
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * Defines how the label is going to be positioned. For RichCheckboxSet or RichRadioSet the start, top and inside values are supported.
   */
  labelEdge?: 'start' | 'top' | 'inside';
  /**
   * Specifies if the parent component is a RichCheckboxSet or RichRadioSet
   */
  parentComponentVariant: 'richRadioSet' | 'richCheckboxSet';
  /**
   * Specifies if there is assistive text or links at the top of the component below the label
   */
  hasTopUserAssistance?: boolean;
  /**
   * Specifies if there is secondary text for the Rich Selection Items
   */
  hasSecondaryText?: boolean;
  /**
   * Specifies if there is media for the Rich Selection Items
   */
  hasMedia?: boolean;
  /**
   * Label content of the layout
   */
  label?: ComponentChildren;
  /**
   * Defines the label width for labelEdge 'start' ('top' is always 100%)
   */
  labelStartWidth?: Size;
  /**
   * Value content of the layout
   */
  children: ComponentChildren;
};

/**
 * A layout component for rendering a label and value pair, used in form components
 * such as RichCheckboxSet and RichRadioSet.
 */
export const RichLabelValueLayout = ({
  columnSpan = 1,
  label,
  labelEdge,
  children,
  labelStartWidth = '33%',
  parentComponentVariant,
  hasTopUserAssistance = false,
  hasSecondaryText = false,
  hasMedia = false
}: Props) => {
  const { baseTheme: radioTheme } = useComponentTheme(RadioRedwoodTheme);
  const { baseTheme: checkboxTheme } = useComponentTheme(CheckboxRedwoodTheme);
  const { baseTheme: labelTheme } = useComponentTheme(LabelRedwoodTheme);
  const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);
  const richSelectionContext = useRichSelectionContext();
  const isRichRadio = parentComponentVariant === 'richRadioSet';
  const isRichCheckbox = parentComponentVariant === 'richCheckboxSet';
  const isStart = labelEdge === 'start';
  const isTop = labelEdge === 'top' || labelEdge === 'inside'; // 'inside' needs to pick up all 'top' styles.
  const isInside = labelEdge === 'inside';

  // JET-68190 - labelStartWidth needs to take columnSpan into account
  const width = computeStartLabelWidth(labelStartWidth, columnSpan);
  const valueWidth = `calc(100% - ${width})`;

  const labelStyles: JSX.CSSProperties = isStart
    ? { flexBasis: width, width: width, maxWidth: width }
    : {};

  const valueStyles: JSX.CSSProperties = isStart
    ? { flexBasis: valueWidth, width: valueWidth, maxWidth: valueWidth }
    : {};

  const { isFormLayout, isReadonly: isFormReadonly } = useFormContext();
  const { isReadonly } = useFormFieldContext();
  const isPureReadonly = (isFormLayout && isFormReadonly) || (!isFormLayout && isReadonly);

  const labelStyleClasses = classNames([
    formLayoutTheme,
    labelSlotStyles.base,
    isStart && labelSlotStyles.start,
    isTop && labelSlotStyles.top,
    isTop && hasTopUserAssistance && labelSlotStyles.topAndTopUA,
    isTop && isPureReadonly && labelSlotStyles.topPureReadonly
  ]);

  const labelInnerClasses = classNames([
    isRichRadio && radioTheme,
    isRichCheckbox && checkboxTheme,
    labelTheme,
    formLayoutTheme,
    labelInnerStyles.base,
    isStart && labelInnerStyles.start,
    isRichRadio && isInside && richLabelInnerStyles.insideRichRadioSet,
    isRichCheckbox && isInside && richLabelInnerStyles.insideRichCheckboxSet,

    // Rich Radioset
    isRichRadio &&
      isStart &&
      !hasTopUserAssistance &&
      richSelectionContext?.layout !== 'xl' &&
      richLabelInnerStyles.startRichRadioSetWithoutTopUA,

    isRichRadio &&
      isStart &&
      !hasTopUserAssistance &&
      richSelectionContext?.layout === 'xl' &&
      richLabelInnerStyles.startXLRichRadioSetWithoutTopUA,

    isRichRadio &&
      isStart &&
      richSelectionContext?.layout === 'md' &&
      !hasTopUserAssistance &&
      !hasSecondaryText &&
      hasMedia &&
      richLabelInnerStyles.startMDRichRadioSetWithoutTopUAOrSecondaryText,

    isRichRadio &&
      isStart &&
      richSelectionContext?.layout === 'md' &&
      !hasTopUserAssistance &&
      !hasSecondaryText &&
      !hasMedia &&
      richLabelInnerStyles.startMDRichRadioSetWithoutMediaTopUAOrSecondaryText,

    isRichRadio &&
      isStart &&
      richSelectionContext?.layout === 'sm' &&
      !hasTopUserAssistance &&
      !hasSecondaryText &&
      hasMedia &&
      richLabelInnerStyles.startSMRichRadioSetWithoutTopUAOrSecondaryText,

    isRichRadio &&
      isStart &&
      richSelectionContext?.layout === 'sm' &&
      !hasTopUserAssistance &&
      !hasSecondaryText &&
      !hasMedia &&
      richLabelInnerStyles.startSMRichRadioSetWithoutMediaTopUAOrSecondaryText,

    // Rich Checkboxset
    isRichCheckbox &&
      isStart &&
      !hasTopUserAssistance &&
      richSelectionContext?.layout !== 'xl' &&
      richLabelInnerStyles.startRichCheckboxSetWithoutTopUA,

    isRichCheckbox &&
      isStart &&
      !hasTopUserAssistance &&
      richSelectionContext?.layout === 'xl' &&
      richLabelInnerStyles.startXLRichCheckboxSetWithoutTopUA,

    isRichCheckbox &&
      isStart &&
      richSelectionContext?.layout === 'md' &&
      !hasTopUserAssistance &&
      !hasSecondaryText &&
      hasMedia &&
      richLabelInnerStyles.startMDRichCheckboxSetWithoutTopUAOrSecondaryText,

    isRichCheckbox &&
      isStart &&
      richSelectionContext?.layout === 'md' &&
      !hasTopUserAssistance &&
      !hasSecondaryText &&
      !hasMedia &&
      richLabelInnerStyles.startMDRichCheckboxSetWithoutMediaTopUAOrSecondaryText,

    isRichCheckbox &&
      isStart &&
      richSelectionContext?.layout === 'sm' &&
      !hasTopUserAssistance &&
      !hasSecondaryText &&
      hasMedia &&
      richLabelInnerStyles.startSMRichCheckboxSetWithoutTopUAOrSecondaryText,

    isRichCheckbox &&
      isStart &&
      richSelectionContext?.layout === 'sm' &&
      !hasTopUserAssistance &&
      !hasSecondaryText &&
      !hasMedia &&
      richLabelInnerStyles.startSMRichCheckboxSetWithoutMediaTopUAOrSecondaryText
  ]);

  return (
    <StyledLabelValueLayout
      // For readonly start aligned labels, we want the div that is the parent of the label to
      // stretch to the height of the value content, including the end content (InputSensitiveText)
      flexAlign={isReadonly && isStart ? 'stretch' : 'start'}
      labelStyleClasses={labelStyleClasses}
      labelStyles={labelStyles}
      labelInnerClasses={labelInnerClasses}
      label={label}
      valueStyles={valueStyles}>
      {children}
    </StyledLabelValueLayout>
  );
};
